import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_TRIALTRACKER_LOAD_BY_ID,
    ACTION_TRIALTRACKER_LOAD_FAILURE,
    ACTION_TRIALTRACKER_LOAD_SUCCESS,

    ACTION_TRIALTRACKER_SAVE,
    ACTION_TRIALTRACKER_SAVE_SUCCESS,
    ACTION_TRIALTRACKER_SAVE_FAILURE,

    ACTION_TRIALTRACKER_CREATE,
    ACTION_TRIALTRACKER_CREATE_SUCCESS,
    ACTION_TRIALTRACKER_CREATE_FAILURE,

    ACTION_TRIALTRACKER_SYNC,
    ACTION_TRIALTRACKER_SYNC_SUCCESS,
    ACTION_TRIALTRACKER_SYNC_FAILURE,

    ACTION_TRIALTRACKER_CLEAR,
    ACTION_TRIALTRACKER_FILTER,

    TrialTrackerAction
} from '../actions/trialTracker';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface ITrialTrackerState {
    formData: Dtos.TrialTracker | Dtos.TrialTracker[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    formRoles: Dtos.RoleType[],
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[],
    loadState: IRequestState;
    saveState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.TrialTracker[];
    formLength: number;
}

const initialState: ITrialTrackerState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    formRoles: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: [],
    formLength: 0
};


function loading(state: ITrialTrackerState, isLoading: boolean): ITrialTrackerState {
    const newState: ITrialTrackerState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(
    state: ITrialTrackerState,
    formData: Dtos.TrialTracker | Dtos.TrialTracker[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    formRoles: Dtos.RoleType[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): ITrialTrackerState {
    const newState: ITrialTrackerState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        formRoles: {
            $set: formRoles
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus?.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus?.message : undefined,
                errors: responseStatus ? responseStatus?.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus?.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function loadFailure(state: ITrialTrackerState, responseStatus: Dtos.ResponseStatus): ITrialTrackerState {
    const newState: ITrialTrackerState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function filter(state: ITrialTrackerState, formFilter: IFormFilter): ITrialTrackerState {

    const items: Dtos.TrialTracker[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formLength = items ? items.length : 0;

    const formFilteredData = items ? items.filter(p => (p.active == true || formFilter.showInActive) &&
        (
            formFilter.search == "" ||
            p.subject.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1
        )) : undefined;

    const newState: ITrialTrackerState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        },
        formLength: {
            $set: formLength
        }
    });

    return newState;
}

function clear(state: ITrialTrackerState, propertiesToClear?: string[]): ITrialTrackerState {

    if (!propertiesToClear) {
        const newState: ITrialTrackerState = update(initialState, {});
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: ITrialTrackerState, isSaving: boolean): ITrialTrackerState {
    const newState: ITrialTrackerState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: ITrialTrackerState,
    formData: Dtos.TrialTracker | Dtos.TrialTracker[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ITrialTrackerState {
    const newState: ITrialTrackerState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus?.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus?.message : undefined,
                errors: responseStatus ? responseStatus?.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus?.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: ITrialTrackerState, responseStatus: Dtos.ResponseStatus): ITrialTrackerState {
    const newState: ITrialTrackerState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus?.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: ITrialTrackerState, isCreating: boolean): ITrialTrackerState {
    const newState: ITrialTrackerState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: ITrialTrackerState,
    formData: Dtos.TrialTracker,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    formRoles: Dtos.RoleType[],
    lookups: Dtos.Lookup[]
): ITrialTrackerState {
    const newState: ITrialTrackerState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        formRoles: {
            $set: formRoles
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: ITrialTrackerState, responseStatus: Dtos.ResponseStatus): ITrialTrackerState {
    const newState: ITrialTrackerState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

const TrialTrackerReducer: Reducer<ITrialTrackerState> = (state: ITrialTrackerState = initialState, action: TrialTrackerAction) => {
    switch (action.type) {
        case ACTION_TRIALTRACKER_LOAD_BY_ID:
            return loading(state, true);
        case ACTION_TRIALTRACKER_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.formRoles, action.lookups, action.responseStatus);
        case ACTION_TRIALTRACKER_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_TRIALTRACKER_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_TRIALTRACKER_SAVE:
            return saving(state, true);
        case ACTION_TRIALTRACKER_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_TRIALTRACKER_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_TRIALTRACKER_CREATE:
            return creating(state, true);
        case ACTION_TRIALTRACKER_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.formRoles, action.lookups);
        case ACTION_TRIALTRACKER_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_TRIALTRACKER_FILTER:
            return filter(state, action.formFilter);
        case ACTION_TRIALTRACKER_SYNC:
            return loading(state, true);
        case ACTION_TRIALTRACKER_SYNC_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.formRoles, action.lookups, action.responseStatus);
        case ACTION_TRIALTRACKER_SYNC_FAILURE:
            return loadFailure(state, action.responseStatus);

    }

    return state;
}

export default TrialTrackerReducer;